// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL RELOJ, LA CUENTA ATRÁS Y EL CRONÓMETRO DE LA CABECERA · LO QUE CUENTA                  (t271, 16-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño (16-oct): «modo reloj con segundero, cuenta atrás, etc. El botón debe estar en la barra superior con un
//  icono de reloj o algo y que abra un modal encima de la página actual, y que cuando se complete la cuenta atrás o algo
//  así, parpadee la pantalla con color». Es para el gabinete y la recepción: un fraguado, un grabado ácido, un
//  blanqueamiento, una espera, sin salir de la pantalla en la que se está.
//
//  AQUÍ VIVE TODO LO QUE CUENTA, SIN REACT. Lo que está en marcha no es de la ventana: la ventana se cierra, se cambia de
//  página, y la cuenta sigue. Por eso el estado es UNO por pestaña del navegador (`elReloj()`), y lo leen el botón de la
//  cabecera (utils/relojCabecera.jsx), la ventana (components/reloj/VentanaReloj.jsx) y el aviso del final
//  (components/reloj/AvisoTiempoCumplido.jsx), que monta la raíz de la app (context/RelojContext.jsx).
//
//  LA CUENTA VA POR LA HORA, NO POR INTERVALOS. Lo que queda es `fin - ahora()` y lo que lleva el cronómetro es
//  `acumulado + (ahora() - inicio)`. Nunca se resta un segundo en cada tic: el navegador frena los temporizadores de una
//  pestaña escondida (a uno por segundo, y a uno por MINUTO pasados cinco minutos), y una cuenta que sumara tics se
//  quedaría atrás justo cuando no se mira. El tic (`latido`, cuatro veces por segundo y solo con algo en marcha) solo
//  repinta y comprueba; el final, además, se programa UNA vez con su hora exacta (`programarFin`) y se vuelve a mirar
//  al volver a la pestaña (la raíz llama a `comprobar` en `visibilitychange`).
//
//  AL LLEGAR A CERO, UNA SOLA VEZ. Cada cuenta que empieza lleva su número (`vuelta`); `comprobar` la da por cumplida
//  solo si esa vuelta no se ha avisado ya (`avisada`), y deja el aviso (`aviso`) para que la raíz lance el parpadeo, el
//  sonido, el título y la notificación. Lo que tiene efectos fuera (el sonido, la notificación) pasa además por
//  `tomarAviso(id)`, que dice que sí UNA vez por aviso: React, en desarrollo, monta dos veces cada efecto.
//
//  DE ESTA PESTAÑA DEL NAVEGADOR, NO DE LA BASE. Se guarda en sessionStorage (sobrevive a recargar la página, no a
//  cerrar la pestaña, y no lo ve ningún otro equipo); la casilla del sonido, en localStorage (es una preferencia de
//  quien usa ese navegador). Todo con try/catch: en una ventana privada o con los datos bloqueados, el reloj funciona
//  igual y simplemente no se recuerda. Si al recargar la cuenta ya había terminado hace un momento (menos de
//  `RECUPERAR_AVISO_MS`), se avisa entonces; si hace más, se deja parada sin sustos.
//
//  SIN HOOKS Y SIN IMPORTS, A PROPÓSITO: la cabecera la montan decenas de guardias con sus dobles, y un `utils/` que no
//  importa nada se carga de verdad en todas (test-helpers/utilDelFrontend). El tiempo, los temporizadores y el almacén se
//  pueden inyectar (`crearReloj({ ahora, temporizador, almacen, preferencias })`): así lo mide
//  backend/routes/reloj-cuenta-atras.test.js sin esperar ni un segundo de verdad.

import { t as i18nT } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const MINUTO = 60 * 1000
// El tope de una cuenta atrás (y de lo que suma «+1 min»): 24 horas. Más no es un temporizador de gabinete.
export const MAXIMO_MS = 24 * 60 * MINUTO
// Si al recargar la página la cuenta había terminado hace menos de esto, se avisa al volver; si hace más, no.
export const RECUPERAR_AVISO_MS = 5 * MINUTO
// El tic de la cabecera mientras algo está en marcha. No cuenta nada: solo repinta y mira si ya es la hora.
export const LATIDO_MS = 250
// Hasta cuántas vueltas guarda el cronómetro.
export const MAX_VUELTAS = 99

export const CLAVE_SESION = 'biango.reloj.v1'
export const CLAVE_SONIDO = 'biango.reloj.sonido'

// Los botones rápidos de la cuenta atrás.
export const DURACIONES_RAPIDAS = [
  { ms: 30 * 1000, texto: '30 s' },
  { ms: 1 * MINUTO, texto: '1 min' },
  { ms: 2 * MINUTO, texto: '2 min' },
  { ms: 3 * MINUTO, texto: '3 min' },
  { ms: 5 * MINUTO, texto: '5 min' },
  { ms: 10 * MINUTO, texto: '10 min' },
  { ms: 15 * MINUTO, texto: '15 min' },
]

export const PESTANAS = ['reloj', 'cuenta', 'crono']

const dos = (n) => String(n).padStart(2, '0')

// «02:41», o «1:02:41» si pasa de la hora. `redondeo` = 'arriba' para una cuenta atrás (a 2 min 40,3 s le quedan
// «02:41»: el cero sale justo al terminar, no un segundo antes) y 'abajo' para el cronómetro.
export function formatoTiempo(ms, redondeo = 'abajo') {
  const v = Math.max(0, Number(ms) || 0)
  const total = redondeo === 'arriba' ? Math.ceil(v / 1000 - 1e-9) : Math.floor(v / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0 ? h + ':' + dos(m) + ':' + dos(s) : dos(m) + ':' + dos(s)
}

// «01:03,27»: el cronómetro con centésimas (con coma, como se escribe en España).
export function formatoCrono(ms) {
  const v = Math.max(0, Math.floor(Number(ms) || 0))
  const cent = Math.floor((v % 1000) / 10)
  return formatoTiempo(v, 'abajo') + ',' + dos(cent)
}

// «3 min», «30 s», «1 min 30 s», «1 h 5 min»: para leerlo en una frase.
export function duracionEnPalabras(ms) {
  const total = Math.max(0, Math.round((Number(ms) || 0) / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const partes = []
  if (h) partes.push(h + ' h')
  if (m) partes.push(m + ' min')
  if (s || !partes.length) partes.push(s + ' s')
  return partes.join(' ')
}

// Minutos y segundos tecleados a mano → ms (lo que no es un número cuenta 0; con su tope).
export function msDeMinutosYSegundos(min, seg) {
  const m = Math.max(0, Math.floor(Number(String(min == null ? '' : min).replace(',', '.')) || 0))
  const s = Math.max(0, Math.floor(Number(String(seg == null ? '' : seg).replace(',', '.')) || 0))
  return Math.min(MAXIMO_MS, (m * 60 + s) * 1000)
}

const acotar = (ms) => Math.max(0, Math.min(MAXIMO_MS, Math.round(Number(ms) || 0)))

export const CUENTA_PARADA = Object.freeze({ estado: 'parada', duracion: 0, total: 0, fin: 0, restante: 0 })
export const CRONO_PARADO = Object.freeze({ estado: 'parada', inicio: 0, acumulado: 0, vueltas: [] })

export function estadoInicial() {
  return { cuenta: CUENTA_PARADA, crono: CRONO_PARADO, vuelta: 0, avisada: 0, aviso: null, ventana: false, pestana: 'reloj', sonido: true }
}

// ── LO QUE SE LEE DEL ESTADO (funciones puras: el tiempo se les da) ─────────────────────────────
// Lo que le queda a la cuenta atrás en el instante `t`.
export function restanteDe(cuenta, t) {
  if (!cuenta) return 0
  if (cuenta.estado === 'marcha') return Math.max(0, cuenta.fin - t)
  if (cuenta.estado === 'pausa') return Math.max(0, cuenta.restante)
  if (cuenta.estado === 'cumplida') return 0
  return Math.max(0, cuenta.duracion)
}
// Lo que lleva el cronómetro en el instante `t`.
export function transcurridoDe(crono, t) {
  if (!crono) return 0
  if (crono.estado === 'marcha') return Math.max(0, crono.acumulado + (t - crono.inicio))
  return Math.max(0, crono.acumulado)
}
// La fracción que le queda al anillo de la cuenta atrás (1 = lleno, 0 = vacío).
export function fraccionRestante(cuenta, t) {
  if (!cuenta) return 0
  if (cuenta.estado === 'parada') return cuenta.duracion > 0 ? 1 : 0
  const total = Math.max(1, cuenta.total || cuenta.duracion || 1)
  return Math.max(0, Math.min(1, restanteDe(cuenta, t) / total))
}
export const hayAlgoEnMarcha = (e) => !!e && (e.cuenta.estado === 'marcha' || e.crono.estado === 'marcha')

// LO QUE ENSEÑA EL BOTÓN DE LA CABECERA. Manda la cuenta atrás (es la que avisa); si no hay, el cronómetro.
//   estado: '' (nada: solo el icono) · 'marcha' · 'pausa' · 'cumplida' · 'crono' (cronómetro en marcha) · 'crono-pausa'
//   que: lo que es, en palabras, para la franja de debajo de la barra (t271b, cuando el botón no cabe)
export function paraElBoton(e, t) {
  const c = e && e.cuenta
  if (c && c.estado === 'cumplida') {
    return { estado: 'cumplida', texto: '00:00', que: i18nT('utils.reloj.tiempo_cumplido', 'Tiempo cumplido'), etiqueta: i18nT('utils.reloj.reloj_tiempo_cumplido_pulsa_para', 'Reloj: tiempo cumplido. Pulsa para abrir el reloj') }
  }
  if (c && (c.estado === 'marcha' || c.estado === 'pausa')) {
    const r = restanteDe(c, t)
    const texto = formatoTiempo(r, 'arriba')
    const pausa = c.estado === 'pausa'
    return {
      estado: pausa ? 'pausa' : 'marcha', texto, que: pausa ? i18nT('utils.reloj.cuenta_atras_en_pausa', 'Cuenta atrás en pausa') : i18nT('utils.reloj.cuenta_atras', 'Cuenta atrás'),
      etiqueta: i18nT('utils.reloj.reloj_cuenta_atras_quedan', 'Reloj: cuenta atrás {{x}}quedan {{duracionEnPalabras}}', { x: (pausa ? i18nT('utils.reloj.en_pausa', 'en pausa, ') : ''), duracionEnPalabras: duracionEnPalabras(Math.ceil(r / 1000) * 1000) }),
    }
  }
  const k = e && e.crono
  if (k && (k.estado === 'marcha' || k.estado === 'pausa')) {
    const v = transcurridoDe(k, t)
    const pausa = k.estado === 'pausa'
    return {
      estado: pausa ? 'crono-pausa' : 'crono', texto: formatoTiempo(v, 'abajo'), que: pausa ? i18nT('utils.reloj.cronometro_en_pausa', 'Cronómetro en pausa') : i18nT('utils.reloj.cronometro', 'Cronómetro'),
      etiqueta: i18nT('utils.reloj.reloj_cronometro_lleva', 'Reloj: cronómetro {{x}}lleva {{duracionEnPalabras}}', { x: (pausa ? i18nT('utils.reloj.en_pausa', 'en pausa, ') : ''), duracionEnPalabras: duracionEnPalabras(Math.floor(v / 1000) * 1000) }),
    }
  }
  return { estado: '', texto: '', que: '', etiqueta: i18nT('utils.reloj.reloj', 'Reloj') }
}

// ── RECUPERAR LO GUARDADO (al recargar la página) ──────────────────────────────────────────────
// Se acepta solo lo que tiene la forma de lo que se guarda: un dato roto o de otra versión se tira y se empieza de cero.
const num = (v) => (typeof v === 'number' && isFinite(v) ? v : 0)
export function recuperar(guardado, t) {
  const e = estadoInicial()
  if (!guardado || typeof guardado !== 'object') return e
  e.vuelta = Math.max(0, Math.floor(num(guardado.vuelta)))
  e.avisada = Math.max(0, Math.floor(num(guardado.avisada)))
  if (PESTANAS.includes(guardado.pestana)) e.pestana = guardado.pestana
  const c = guardado.cuenta
  if (c && typeof c === 'object' && ['parada', 'marcha', 'pausa', 'cumplida'].includes(c.estado)) {
    const duracion = acotar(c.duracion)
    const total = acotar(c.total) || duracion
    if (c.estado === 'marcha' && num(c.fin) > 0) {
      const fin = num(c.fin)
      if (fin > t) e.cuenta = { estado: 'marcha', duracion, total, fin, restante: 0 }
      // TERMINÓ MIENTRAS SE RECARGABA. Si fue hace un momento y no se avisó, sigue «en marcha» con su hora de fin:
      // la primera comprobación la da por cumplida y avisa. Si fue hace rato, se queda parada, sin sustos.
      else if (t - fin <= RECUPERAR_AVISO_MS && e.avisada !== e.vuelta) e.cuenta = { estado: 'marcha', duracion, total, fin, restante: 0 }
      else e.cuenta = { estado: 'parada', duracion, total: duracion, fin: 0, restante: 0 }
    } else if (c.estado === 'pausa') {
      e.cuenta = { estado: 'pausa', duracion, total, fin: 0, restante: acotar(c.restante) }
    } else {
      // Parada, o cumplida y ya avisada: lista para volver a empezar con lo mismo.
      e.cuenta = { estado: 'parada', duracion, total: duracion, fin: 0, restante: 0 }
    }
  }
  const k = guardado.crono
  if (k && typeof k === 'object' && ['parada', 'marcha', 'pausa'].includes(k.estado)) {
    const vueltas = Array.isArray(k.vueltas)
      ? k.vueltas.filter((v) => v && typeof v === 'object').slice(0, MAX_VUELTAS).map((v) => ({ total: Math.max(0, num(v.total)), parcial: Math.max(0, num(v.parcial)) }))
      : []
    if (k.estado === 'marcha' && num(k.inicio) > 0 && num(k.inicio) <= t) e.crono = { estado: 'marcha', inicio: num(k.inicio), acumulado: Math.max(0, num(k.acumulado)), vueltas }
    else if (k.estado === 'pausa' || k.estado === 'marcha') e.crono = { estado: 'pausa', inicio: 0, acumulado: Math.max(0, num(k.acumulado)), vueltas }
  }
  return e
}

// Lo que se guarda: lo que cuenta, no la ventana ni el aviso (al recargar, la ventana sale cerrada).
export function paraGuardar(e) {
  return { cuenta: e.cuenta, crono: e.crono, vuelta: e.vuelta, avisada: e.avisada, pestana: e.pestana }
}

// ── LOS ALMACENES DE VERDAD, CON SU TRY/CATCH ───────────────────────────────────────────────────
function almacenDe(nombre) {
  const caja = () => { try { return typeof window !== 'undefined' && window[nombre] ? window[nombre] : null } catch (_) { return null } }
  return {
    leer(clave) { try { const c = caja(); return c ? c.getItem(clave) : null } catch (_) { return null } },
    escribir(clave, valor) { try { const c = caja(); if (c) c.setItem(clave, valor) } catch (_) { /* sin almacén: no se recuerda */ } },
  }
}
const TEMPORIZADOR_DE_VERDAD = {
  poner: (f, ms) => setTimeout(f, ms),
  quitar: (id) => clearTimeout(id),
  cada: (f, ms) => setInterval(f, ms),
  parar: (id) => clearInterval(id),
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL RELOJ: el estado, quien lo escucha y las acciones
// ══════════════════════════════════════════════════════════════════════════════════════════════
export function crearReloj(opciones = {}) {
  const ahora = typeof opciones.ahora === 'function' ? opciones.ahora : () => Date.now()
  const tmp = opciones.temporizador || TEMPORIZADOR_DE_VERDAD
  const sesion = opciones.almacen || almacenDe('sessionStorage')
  const prefs = opciones.preferencias || almacenDe('localStorage')

  let guardado = null
  try { const t = sesion.leer(CLAVE_SESION); guardado = t ? JSON.parse(t) : null } catch (_) { guardado = null }
  let e = recuperar(guardado, ahora())
  try { if (prefs.leer(CLAVE_SONIDO) === '0') e = Object.assign({}, e, { sonido: false }) } catch (_) { /* con sonido */ }

  const oyentes = new Set()
  const latidos = new Set()
  const tomados = new Set()
  let finId = null
  let latidoId = null

  const guardar = () => { try { sesion.escribir(CLAVE_SESION, JSON.stringify(paraGuardar(e))) } catch (_) { /* no se recuerda */ } }
  const avisarOyentes = () => { for (const f of [...oyentes]) { try { f(e) } catch (_) { /* un oyente roto no para el reloj */ } } }

  // EL FINAL, CON SU HORA EXACTA. Un solo temporizador puesto desde la acción (no encadenado dentro del tic): es el que
  // el navegador menos frena en una pestaña escondida. Si aun así llega tarde, `comprobar` lo da por cumplido igual.
  function programarFin() {
    if (finId != null) { try { tmp.quitar(finId) } catch (_) { /* ya no estaba */ } finId = null }
    if (e.cuenta.estado === 'marcha') finId = tmp.poner(() => { finId = null; comprobar() }, Math.max(0, e.cuenta.fin - ahora()) + 20)
  }
  function programarLatido() {
    const hace = hayAlgoEnMarcha(e)
    if (hace && latidoId == null) latidoId = tmp.cada(latir, LATIDO_MS)
    else if (!hace && latidoId != null) { try { tmp.parar(latidoId) } catch (_) { /* ya no estaba */ } latidoId = null }
  }
  function latir() {
    comprobar()
    for (const f of [...latidos]) { try { f() } catch (_) { /* un pintor roto no para el reloj */ } }
  }
  function cambiar(cambios, { sinGuardar = false } = {}) {
    e = Object.assign({}, e, cambios)
    if (!sinGuardar) guardar()
    programarFin()
    programarLatido()
    avisarOyentes()
    return e
  }

  // ¿Ha llegado a cero? Lo da por cumplido UNA vez por cuenta (su `vuelta`), y deja el aviso para la raíz.
  function comprobar() {
    const c = e.cuenta
    if (c.estado !== 'marcha') return false
    const t = ahora()
    if (c.fin > t) return false
    // Ya avisada (no debería quedar en marcha, pero por si acaso): se para sin volver a avisar.
    if (e.avisada === e.vuelta) {
      cambiar({ cuenta: { estado: 'parada', duracion: c.duracion, total: c.duracion, fin: 0, restante: 0 } })
      return false
    }
    cambiar({
      cuenta: Object.assign({}, c, { estado: 'cumplida', restante: 0 }),
      avisada: e.vuelta,
      aviso: { id: e.vuelta, total: c.total || c.duracion, fin: c.fin, cuando: t },
    })
    return true
  }

  const reloj = {
    ahora,
    estado: () => e,
    suscribir(f) { oyentes.add(f); return () => { oyentes.delete(f) } },
    alLatir(f) { latidos.add(f); return () => { latidos.delete(f) } },
    comprobar,
    // Para lo que tiene efectos fuera (sonido, notificación): sí UNA vez por aviso.
    tomarAviso(id) { if (id == null || tomados.has(id)) return false; tomados.add(id); return true },

    // ── la ventana ──
    abrir(pestana) {
      const p = PESTANAS.includes(pestana) ? pestana
        : (e.cuenta.estado !== 'parada' ? 'cuenta' : (e.crono.estado !== 'parada' ? 'crono' : e.pestana))
      return cambiar({ ventana: true, pestana: p })
    },
    cerrar() { return cambiar({ ventana: false }) },
    elegirPestana(p) { return PESTANAS.includes(p) ? cambiar({ pestana: p }) : e },
    ponerSonido(si) {
      try { prefs.escribir(CLAVE_SONIDO, si ? '1' : '0') } catch (_) { /* no se recuerda */ }
      return cambiar({ sonido: !!si }, { sinGuardar: true })
    },

    // ── la cuenta atrás ──
    // Elegir lo que va a durar (con la cuenta parada o ya cumplida; en marcha no se toca sin «Reiniciar»).
    elegirDuracion(ms) {
      if (e.cuenta.estado === 'marcha' || e.cuenta.estado === 'pausa') return e
      const d = acotar(ms)
      return cambiar({ cuenta: { estado: 'parada', duracion: d, total: d, fin: 0, restante: 0 }, aviso: null })
    },
    // Empezar: con la duración que se le dé o con la elegida. La hora de fin se fija AHORA y ya no se toca más que con
    // «Pausa» (que guarda lo que queda) o «+1 min».
    empezarCuenta(ms) {
      const d = ms != null ? acotar(ms) : e.cuenta.duracion
      if (!(d > 0)) return e
      const t = ahora()
      return cambiar({ cuenta: { estado: 'marcha', duracion: d, total: d, fin: t + d, restante: 0 }, vuelta: e.vuelta + 1, aviso: null })
    },
    pausarCuenta() {
      const c = e.cuenta
      if (c.estado !== 'marcha') return e
      const r = c.fin - ahora()
      if (r <= 0) { comprobar(); return e }
      return cambiar({ cuenta: Object.assign({}, c, { estado: 'pausa', restante: r, fin: 0 }) })
    },
    seguirCuenta() {
      const c = e.cuenta
      if (c.estado !== 'pausa') return e
      return cambiar({ cuenta: Object.assign({}, c, { estado: 'marcha', fin: ahora() + c.restante, restante: 0 }) })
    },
    reiniciarCuenta() {
      const d = e.cuenta.duracion
      return cambiar({ cuenta: { estado: 'parada', duracion: d, total: d, fin: 0, restante: 0 }, aviso: null })
    },
    // «+1 min»: en marcha o en pausa alarga la que hay; parada suma a lo elegido; cumplida, empieza un minuto más.
    masUnMinuto() {
      const c = e.cuenta
      if (c.estado === 'marcha') {
        const fin = Math.min(c.fin + MINUTO, ahora() + MAXIMO_MS)
        return cambiar({ cuenta: Object.assign({}, c, { fin, total: acotar(c.total + (fin - c.fin)) }) })
      }
      if (c.estado === 'pausa') {
        const restante = acotar(c.restante + MINUTO)
        return cambiar({ cuenta: Object.assign({}, c, { restante, total: acotar(c.total + (restante - c.restante)) }) })
      }
      if (c.estado === 'cumplida') {
        const t = ahora()
        return cambiar({ cuenta: { estado: 'marcha', duracion: c.duracion, total: MINUTO, fin: t + MINUTO, restante: 0 }, vuelta: e.vuelta + 1, aviso: null })
      }
      const d = acotar(c.duracion + MINUTO)
      return cambiar({ cuenta: { estado: 'parada', duracion: d, total: d, fin: 0, restante: 0 } })
    },
    // «Parar» del aviso: se apaga todo (parpadeo, sonido, título) y la cuenta queda lista para volver a empezar.
    pararAviso() {
      const c = e.cuenta
      const cuenta = c.estado === 'cumplida' ? { estado: 'parada', duracion: c.duracion, total: c.duracion, fin: 0, restante: 0 } : c
      return cambiar({ aviso: null, cuenta })
    },

    // ── el cronómetro ──
    empezarCrono() {
      if (e.crono.estado !== 'parada') return e
      return cambiar({ crono: { estado: 'marcha', inicio: ahora(), acumulado: 0, vueltas: [] } })
    },
    pausarCrono() {
      const k = e.crono
      if (k.estado !== 'marcha') return e
      return cambiar({ crono: Object.assign({}, k, { estado: 'pausa', acumulado: transcurridoDe(k, ahora()), inicio: 0 }) })
    },
    seguirCrono() {
      const k = e.crono
      if (k.estado !== 'pausa') return e
      return cambiar({ crono: Object.assign({}, k, { estado: 'marcha', inicio: ahora() }) })
    },
    // Una vuelta (o parcial): lo que lleva en total y lo que ha pasado desde la anterior.
    vueltaCrono() {
      const k = e.crono
      if (k.estado !== 'marcha' || k.vueltas.length >= MAX_VUELTAS) return e
      const total = transcurridoDe(k, ahora())
      const anterior = k.vueltas.length ? k.vueltas[k.vueltas.length - 1].total : 0
      return cambiar({ crono: Object.assign({}, k, { vueltas: k.vueltas.concat([{ total, parcial: Math.max(0, total - anterior) }]) }) })
    },
    reiniciarCrono() { return cambiar({ crono: CRONO_PARADO }) },
  }

  // Lo recuperado al recargar sigue contando (y si terminó hace un momento, avisa en la primera comprobación).
  programarFin()
  programarLatido()
  return reloj
}

// EL RELOJ DE LA APP: uno por pestaña del navegador, creado la primera vez que alguien lo pide (no al importar).
let _reloj = null
export function elReloj() {
  if (!_reloj) _reloj = crearReloj()
  return _reloj
}
// Abrir la ventana desde cualquier sitio (el botón de la cabecera).
export function abrirReloj(pestana) { return elReloj().abrir(pestana) }
